import { useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Paperclip, Download, Trash2, Upload } from "lucide-react";
import api, { API, fmtDate, fmtIDR } from "@/lib_app/api";
import { fileIcon, humanSize } from "@/components/app/DealModal";
import { toast } from "sonner";

const ALLOWED = /\.(pdf|jpe?g|png|gif|webp|docx?|xlsx?|csv|txt)$/i;
const MAX_SIZE = 15 * 1024 * 1024;

export default function AttachmentPopover({ prospect, canEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [qtns, setQtns] = useState([]);
  const fileRef = useRef();
  const atts = prospect?.attachments || [];
  const qtnCount = prospect?.quotations_count || 0;
  const total = atts.length + qtnCount;

  const loadQuotations = () => {
    if (!qtnCount) return;
    api.get("/quotations", { params: { prospect_id: prospect.id } })
      .then((r) => setQtns(r.data || []))
      .catch(() => {});
  };

  const openQuotationPdf = (q) => {
    const token = localStorage.getItem("sp_token");
    window.open(`${API}/quotations/${q.id}/pdf?auth=${encodeURIComponent(token || "")}`, "_blank", "noopener");
  };

  const openFile = async (att) => {
    try {
      const token = localStorage.getItem("sp_token");
      const url = `${API}/prospects/${prospect.id}/attachments/${att.id}/download?auth=${encodeURIComponent(token || "")}`;
      window.open(url, "_blank", "noopener");
    } catch {
      toast.error("Gagal buka file");
    }
  };

  const uploadFiles = async (fileList) => {
    const arr = Array.from(fileList || []);
    const valid = [];
    arr.forEach((f) => {
      if (!ALLOWED.test(f.name)) return toast.error(`${f.name}: ekstensi tidak diizinkan`);
      if (f.size > MAX_SIZE) return toast.error(`${f.name}: >15MB`);
      valid.push(f);
    });
    if (!valid.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      valid.forEach((f) => fd.append("files", f));
      await api.post(`/prospects/${prospect.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${valid.length} lampiran diunggah`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal upload");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (att) => {
    if (!window.confirm(`Hapus lampiran "${att.filename}"?`)) return;
    try {
      await api.delete(`/prospects/${prospect.id}/attachments/${att.id}`);
      toast.success("Lampiran dihapus");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    }
  };

  return (
    <Popover onOpenChange={(o) => { if (o) loadQuotations(); }}>
      <PopoverTrigger asChild>
        <button
          data-testid={`att-btn-${prospect.id}`}
          title={`${atts.length} lampiran · ${qtnCount} quotation`}
          className={`relative w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-[#F4F5F7] transition-colors ${
            total > 0 ? "text-[#090808]" : "text-[#A0A3A8] hover:text-[#626061]"
          }`}
        >
          <Paperclip className="w-4 h-4" />
          {total > 0 && (
            <span data-testid={`att-count-${prospect.id}`} className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#090808] text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
              {total}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 rounded-2xl card-soft border-0" align="end" data-testid={`att-popover-${prospect.id}`}>
        <div className="px-4 py-3 border-b border-[#DCE0E7] flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-[#090808]">Lampiran</div>
            <div className="text-xs text-[#A0A3A8] mt-0.5">{atts.length} file{qtnCount ? ` · ${qtnCount} quotation` : ""}</div>
          </div>
          {canEdit && (
            <>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
                className="hidden"
                onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                data-testid={`att-upload-${prospect.id}`}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#090808] hover:bg-[#000000] text-white text-xs font-semibold disabled:opacity-50"
              >
                <Upload className="w-3.5 h-3.5" /> {busy ? "..." : "Upload"}
              </button>
            </>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {atts.length === 0 && qtnCount === 0 ? (
            <div className="px-4 py-10 text-center text-xs text-[#A0A3A8]">Belum ada lampiran.</div>
          ) : atts.map((a) => {
            const Icon = fileIcon(a.mime, a.filename);
            return (
              <div key={a.id} className="px-4 py-3 border-b border-[#DCE0E7] last:border-0 flex items-center gap-3" data-testid={`att-item-${a.id}`}>
                <div className="w-9 h-9 rounded-xl bg-[#F4F5F7] text-[#090808] flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <button onClick={() => openFile(a)} className="flex-1 min-w-0 text-left" data-testid={`att-open-${a.id}`}>
                  <div className="text-sm font-medium text-[#090808] truncate hover:text-[#090808]">{a.filename}</div>
                  <div className="text-[11px] text-[#A0A3A8]">{humanSize(a.size)} · {fmtDate(a.uploaded_at)} · {a.uploaded_by_name}</div>
                </button>
                <button
                  onClick={() => openFile(a)}
                  title="Download / Open"
                  data-testid={`att-download-${a.id}`}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-md hover:bg-[#F4F5F7] text-[#626061] hover:text-[#090808]"
                >
                  <Download className="w-4 h-4" />
                </button>
                {canEdit && (
                  <button
                    onClick={() => remove(a)}
                    title="Hapus"
                    data-testid={`att-delete-${a.id}`}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-md hover:bg-red-50 text-[#626061] hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
          {qtnCount > 0 && (
            <div data-testid={`att-quotations-${prospect.id}`}>
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold border-t border-[#DCE0E7]">
                Dokumen Quotation ({qtnCount})
              </div>
              {qtns.map((q) => (
                <button
                  key={q.id} onClick={() => openQuotationPdf(q)}
                  data-testid={`att-quotation-${q.id}`}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-[#F9FAFC] border-b border-[#DCE0E7] last:border-0"
                >
                  <div className="w-9 h-9 rounded-xl bg-[#FDE7E9] text-[#CE6968] text-[10px] font-bold flex items-center justify-center shrink-0">PDF</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#090808] truncate font-mono">{q.number}</div>
                    <div className="text-[11px] text-[#A0A3A8]">{fmtDate(q.date)} · {fmtIDR(q.grand_total)}</div>
                  </div>
                  <Download className="w-4 h-4 text-[#626061] shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
